/**
 * Navigation Service - Phase 4 Architecture Compliance
 * Implements centralized navigation management with service registry integration
 */

import type { 
  NavSection, 
  NavItem, 
  NavigationConfig,
  NavigationState,
  INavigationService,
  ServiceResponse,
  IService
} from '@flowstate/types';

// ============================================================================
// Helper Functions - Compatible with @flowstate/types ServiceResponse
// ============================================================================

/**
 * Create a successful service response
 */
function createSuccessResponse<T>(data: T): ServiceResponse<T> {
  return {
    success: true,
    data,
  };
}

/**
 * Create an error service response (still requires data for type safety)
 */
function createErrorResponse<T>(data: T, errorMessage: string): ServiceResponse<T> {
  return {
    success: false,
    data,
    error: errorMessage,
  };
}

/**
 * Navigation Service Implementation
 * Centralized navigation management with caching and performance optimization
 * Implements the INavigationService interface
 */
export class NavigationService implements INavigationService {
  readonly name = 'navigation' as const;
  
  private sections: NavSection[] = [];
  private state: NavigationState = {
    sections: [],
    activeItem: null,
    expandedSections: new Set(),
    searchQuery: '',
    mobileOpen: false,
    searchFocused: false,
  };
  private config: NavigationConfig = {
    sections: [],
    defaultExpanded: ['dashboard', 'core'],
    searchEnabled: true,
    keyboardShortcuts: true,
    theme: 'auto',
    maxCollapsedItems: 5,
  };
  private badgeCounts = new Map<string, number>();
  private listeners = new Set<(state: NavigationState) => void>();
  
  /**
   * Initialize the navigation service
   */
  async initialize(): Promise<void> {
    try {
      // Load initial navigation configuration
      await this.loadNavigationConfig();
      
      // Set up event listeners
      this.setupEventListeners();
    } catch (error) {
      console.error('❌ NavigationService initialization failed:', error);
      throw error;
    }
  }
  
  /**
   * Dispose of the navigation service
   */
  async dispose(): Promise<void> {
    // Clean up listeners
    this.listeners.clear();
    this.badgeCounts.clear();
  }
  
  /**
   * Get all navigation sections
   */
  async getSections(): Promise<ServiceResponse<NavSection[]>> {
    try {
      return createSuccessResponse(this.sections);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<NavSection[]>([], `NAVIGATION_SECTIONS_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Update badge count for a specific navigation item
   */
  async updateBadgeCount(itemId: string, count: number): Promise<ServiceResponse<void>> {
    try {
      this.badgeCounts.set(itemId, count);
      
      // Update the item in sections
      this.updateItemBadge(itemId, count);
      
      // Notify listeners
      this.notifyListeners();
      
      return createSuccessResponse(undefined as void);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<void>(undefined as void, `NAVIGATION_BADGE_UPDATE_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Set the active navigation item
   */
  async setActiveItem(itemId: string): Promise<ServiceResponse<void>> {
    try {
      this.state.activeItem = itemId;
      this.notifyListeners();
      
      return createSuccessResponse(undefined as void);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<void>(undefined as void, `NAVIGATION_ACTIVE_ITEM_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Toggle section expanded/collapsed state
   */
  async toggleSection(sectionId: string): Promise<ServiceResponse<void>> {
    try {
      const newExpandedSections = new Set(this.state.expandedSections);
      
      if (newExpandedSections.has(sectionId)) {
        newExpandedSections.delete(sectionId);
      } else {
        newExpandedSections.add(sectionId);
      }
      
      this.state.expandedSections = newExpandedSections;
      this.notifyListeners();
      
      return createSuccessResponse(undefined as void);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<void>(undefined as void, `NAVIGATION_TOGGLE_SECTION_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Get current navigation state
   */
  async getNavigationState(): Promise<ServiceResponse<NavigationState>> {
    try {
      return createSuccessResponse({ ...this.state });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<NavigationState>(this.state, `NAVIGATION_STATE_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Update navigation configuration
   */
  async updateConfig(config: Partial<NavigationConfig>): Promise<ServiceResponse<void>> {
    try {
      this.config = { ...this.config, ...config };
      
      // Update state if sections changed
      if (config.sections) {
        this.sections = config.sections;
        this.state.sections = config.sections;
      }
      
      this.notifyListeners();
      
      return createSuccessResponse(undefined as void);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<void>(undefined as void, `NAVIGATION_CONFIG_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Search navigation items
   */
  async searchItems(query: string): Promise<ServiceResponse<NavItem[]>> {
    try {
      if (!query.trim()) {
        return createSuccessResponse([]);
      }
      
      const results: NavItem[] = [];
      const searchTerm = query.toLowerCase();
      
      for (const section of this.sections) {
        for (const item of section.items) {
          if (item.label.toLowerCase().includes(searchTerm)) {
            results.push(item);
          }
          
          // Search children
          if (item.children) {
            for (const child of item.children) {
              if (child.label.toLowerCase().includes(searchTerm)) {
                results.push(child);
              }
            }
          }
        }
      }
      
      return createSuccessResponse(results);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse<NavItem[]>([], `NAVIGATION_SEARCH_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Get navigation statistics
   */
  async getNavigationStats(): Promise<ServiceResponse<{
    totalSections: number;
    totalItems: number;
    activeItem: string | null;
    expandedSections: string[];
    searchQuery: string;
  }>> {
    try {
      const totalItems = this.sections.reduce((count: number, section) => {
        return count + section.items.length + 
          section.items.reduce((childCount: number, item: NavItem) => {
            return childCount + (item.children?.length || 0);
          }, 0);
      }, 0);
      
      return createSuccessResponse({
        totalSections: this.sections.length,
        totalItems,
        activeItem: this.state.activeItem,
        expandedSections: Array.from(this.state.expandedSections),
        searchQuery: this.state.searchQuery,
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return createErrorResponse({
        totalSections: 0,
        totalItems: 0,
        activeItem: null,
        expandedSections: [],
        searchQuery: '',
      }, `NAVIGATION_STATS_ERROR: ${errorMsg}`);
    }
  }
  
  /**
   * Subscribe to navigation state changes
   */
  subscribe(listener: (state: NavigationState) => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  /**
   * Load navigation configuration
   */
  private async loadNavigationConfig(): Promise<void> {
    // In a real implementation, this would load from a config file or API
    // For now, we'll use default configuration
    this.config = {
      sections: [],
      defaultExpanded: ['dashboard', 'core'],
      searchEnabled: true,
      keyboardShortcuts: true,
      theme: 'auto',
      maxCollapsedItems: 5,
    };
  }
  
  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    // Set up keyboard shortcuts if enabled
    if (this.config.keyboardShortcuts) {
      document.addEventListener('keydown', this.handleKeyboardShortcuts.bind(this));
    }
  }
  
  /**
   * Handle keyboard shortcuts
   */
  private handleKeyboardShortcuts(event: KeyboardEvent): void {
    // Implement keyboard shortcuts for navigation
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'k':
          event.preventDefault();
          // Focus search
          break;
        case '1':
          event.preventDefault();
          // Navigate to first section
          break;
        // Add more shortcuts as needed
      }
    }
  }
  
  /**
   * Update item badge count
   */
  private updateItemBadge(itemId: string, count: number): void {
    for (const section of this.sections) {
      for (const item of section.items) {
        if (item.id === itemId) {
          item.badge = count;
          return;
        }
        
        // Check children
        if (item.children) {
          for (const child of item.children) {
            if (child.id === itemId) {
              child.badge = count;
              return;
            }
          }
        }
      }
    }
  }
  
  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener({ ...this.state });
      } catch (error) {
        console.error('Error in navigation state listener:', error);
      }
    });
  }
}

/**
 * Create navigation service instance
 */
export const navigationService = new NavigationService();
